import type { WorkspaceImage } from './server-context.js';
export type { WorkspaceImage } from './server-context.js';
import type { InteractiveCapability } from '@ndrc/mini-sdk';
export type {
  InteractiveCapability,
  MiniSnapshot,
  MiniSession,
  Answer,
  MiniManifest,
} from '@ndrc/mini-sdk';
// packages/app-sdk/src/client.ts
export {
  readLocal,
  writeLocal,
  useLocalState,
  notifyDataChanged,
  subscribeDataChanges,
  useOnline,
} from './browser-state.js';
import { createContext, createElement, useContext } from 'react';
import type { ComponentType, ReactNode } from 'react';
import type { EntityAction, PermissionKey, WorkspaceKind } from './manifest.js';
import type { Scope } from './server.js';

export { Link, Routes, Route, useNavigate, useParams, Outlet, useSearchParams } from 'react-router';

/**
 * Le contexte qu'une app reçoit — **et il ne contient que ce que le bureau sait réellement
 * fournir aujourd'hui**.
 *
 * La règle du dépôt reste entière : rien ici ne renvoie une valeur vide qui aurait l'air juste.
 * Elle est simplement tenue par le compilateur au lieu de l'être par une erreur d'exécution. Une
 * première rédaction exposait les champs à venir derrière un `Proxy` dont le piège `get` levait,
 * pour qu'un `ctx.data` oublié se voie tout de suite. Le mécanisme était faux : **un accès en
 * lecture ne doit jamais lever**. React (son journal de rendu, React DevTools, un `console.log`,
 * une décomposition `{...ctx}`) parcourt les objets qu'on lui confie ; l'erreur partait donc toute
 * seule, hors de tout code d'app, et laissait React dans un état corrompu (« Should not already be
 * working »). Le bureau devenait inutilisable en classe.
 *
 * Une clé absente, elle, rend `undefined` — comportement JavaScript ordinaire, que React sait
 * traverser — et `ctx.data` refuse désormais de **compiler** : la faute est trouvée par
 * `pnpm typecheck`, avant la classe, au lieu du premier rendu.
 *
 * Ce que les chantiers suivants rebrancheront ici, nommément :
 *
 * - chantier 8 (temps réel) : `events`, `realtime`, `notifications` ;
 * - chantier 10 (première application) : `company`, `data`, `intents`, `files`, `settings`,
 *   `theme` — et les paramètres de type `<P, S>` qui typaient les deux derniers.
 *
 * Le contexte SERVEUR (`ServerContext`, `./server.ts`) suit la même règle par l'autre voie : ses
 * champs non branchés sont de vraies fonctions, qui lèvent **quand on les appelle**.
 */

/** Gravité d'une remarque : « error » désigne une faute, « warning » un point à regarder. */
export type ReviewSeverity = 'error' | 'warning';

/**
 * Une remarque de relecture, TOUJOURS rattachée à la fiche qu'elle concerne : `subjectId` est
 * l'identifiant de cette fiche (`core.contacts.id` — celui-là même qu'une app affiche déjà dans sa
 * grille), ce qui lui permet de DÉSIGNER la ligne en cause au lieu de laisser l'élève la chercher.
 */
export interface ReviewRemark {
  subjectId: string;
  /** Le nom lisible de la fiche, tel que l'élève la reconnaît — jamais un identifiant technique. */
  subjectLabel: string;
  /** Le champ en cause, en français (« adresse e-mail », « téléphone »). */
  field: string;
  /** La phrase lue par l'élève : ce qui ne va pas, et où. */
  message: string;
  severity: ReviewSeverity;
}

/**
 * Ce que la relecture rend — **deux natures, jamais mélangées**, parce qu'elles n'ont pas la même
 * valeur pour un élève :
 *
 * - `wrong` : ce qui est **mal écrit**, trouvé par des règles. Une adresse sans « @ » est fausse
 *   pour tout le monde, toujours. À corriger.
 * - `doubtful` : ce qu'un **modèle de langue** trouve douteux. Une opinion, utile et faillible.
 *   À vérifier soi-même — jamais à présenter comme une faute constatée.
 *
 * Une app qui afficherait les deux dans une seule liste ferait exactement l'erreur que cette
 * séparation existe pour empêcher.
 *
 * `looked`/`notLooked` sont les phrases françaises de ce qui a été regardé, et de ce qui ne l'a
 * pas été. Elles viennent du cœur, dérivées des règles elles-mêmes : les recopier dans les textes
 * d'une app la ferait mentir le jour où une règle change.
 */
export interface WorkReview {
  /** Nombre de fiches relues. */
  reviewed: number;
  wrong: readonly ReviewRemark[];
  doubtful: readonly ReviewRemark[];
  looked: readonly string[];
  notLooked: readonly string[];
  /** `null` quand la relecture par jugement a bien eu lieu. Sinon, la phrase française qui dit
   * pourquoi elle n'a pas eu lieu et quoi faire — à afficher telle quelle, jamais réécrite. */
  doubtfulUnavailable: string | null;
}

/**
 * **Le correspondant d'un appel simulé, tel qu'une app le montre AVANT de décrocher** (ADR-015).
 *
 * Ce que le cœur donne d'un personnage, et rien de plus : son identité, sa situation, son humeur.
 * Le brief joué par le modèle — ce que ce client ne dira que si l'élève le lui demande — ne
 * traverse JAMAIS le réseau. Une app qui pourrait le lire donnerait la réponse à l'élève avant sa
 * première question.
 */
export interface CallPersona {
  id: string;
  /** Le nom sous lequel il s'annonce au téléphone. */
  name: string;
  /** Son entreprise à lui — jamais celle de l'élève. */
  company: string;
  role: string;
  /** `inbound` : il appelle l'élève. `outbound` : c'est l'élève qui l'appelle. */
  direction: 'inbound' | 'outbound';
  /** La situation en une phrase, à afficher avant de décrocher. */
  situation: string;
  mood: string;
  /**
   * Le timbre souhaité — **le seul indice destiné à la voix de synthèse**, et il est délibérément
   * abstrait : ni nom de voix, ni fournisseur. Le jour où la voix du navigateur sera remplacée par
   * une voix payante, c'est cette valeur-là que la nouvelle implémentation traduira.
   */
  voice: 'feminine' | 'masculine';
}

/** Un tour de parole. `studentTurn` numérote les répliques de l'ÉLÈVE (1 = la première) : c'est le
 * repère auquel le retour de fin d'appel se réfère, et le seul que l'élève retrouve à l'écran. */
export interface CallMessage {
  id: string;
  speaker: 'client' | 'student';
  text: string;
  at: string;
  studentTurn: number | null;
}

export interface CallDebriefEntry {
  /** Le tour de parole de l'élève que ce constat désigne. */
  tour: number;
  constat: string;
}

/**
 * Le retour de fin d'appel. **Aucun champ où écrire une note** — ni score, ni niveau, ni mention :
 * l'enseignant l'a exclu, et l'exclusion tient dans la forme du type plutôt que dans une consigne
 * qu'on pourrait oublier. `looked`/`notLooked` sont les phrases françaises de ce que le retour a
 * regardé et de ce qu'il ne pouvait pas regarder ; elles viennent du cœur, et une app qui les
 * recopierait la ferait mentir le jour où la consigne change.
 */
export interface CallDebrief {
  reussi: readonly CallDebriefEntry[];
  autrement: readonly CallDebriefEntry[];
  questions: readonly string[];
  looked: readonly string[];
  notLooked: readonly string[];
}

export interface CallSession {
  mode?: 'scripted' | 'ai';
  id: string;
  persona: CallPersona;
  status: 'open' | 'ended';
  startedAt: string;
  endedAt: string | null;
  messages: readonly CallMessage[];
  /** Répliques que l'élève peut encore écrire dans cet appel. */
  turnsLeft: number;
  /** Écrit au moment de raccrocher ; `null` tant que l'appel dure. */
  debrief: CallDebrief | null;
  /** La phrase française du cœur quand le retour n'a pas pu être écrit (plafond de dépense atteint,
   * usage coupé, panne du fournisseur) — à afficher telle quelle. L'échange, lui, reste relisible. */
  debriefUnavailable: string | null;
}

export interface CallState {
  /** `null` : aucun appel n'a encore eu lieu dans cet espace. */
  call: CallSession | null;
  /** Pourquoi le client n'a pas pu parler, s'il y a lieu. Jamais une exception : un refus attendu
   * (aucune clé, plafond atteint, usage coupé) laisse l'écran debout. */
  unavailable: string | null;
}

/**
 * **La SECONDE capacité du cœur ouverte aux applications** (ADR-015) — un entretien téléphonique
 * simulé, où un modèle de langue joue le client et l'élève lui répond par écrit.
 *
 * Elle suit à la lettre la règle posée par l'ADR-014 : une capacité NOMMÉE, aussi étroite que ce
 * qu'elle doit faire. **Aucune de ces cinq méthodes n'accepte d'identifiant d'appel, d'élève,
 * d'espace de travail ou de classe** — il n'existe aucun endroit où une app pourrait en désigner
 * un. Le cœur résout l'espace depuis la SESSION de celui qui parle, et l'appel visé est toujours
 * celui de cet espace-là.
 *
 * Une app ne choisit pas non plus ce que le client dit : elle nomme un personnage du catalogue
 * (`personas()`), et c'est le cœur qui détient sa consigne. Un persona est une consigne système
 * déguisée ; le laisser écrire par une app rouvrirait exactement le canal générique que l'ADR-014
 * a fermé.
 */
export interface CallCapability {
  /** Le catalogue proposé par le cœur, et l'identifiant de celui qui est choisi par défaut. */
  personas(): Promise<{ personas: readonly CallPersona[]; defaultId: string }>;
  /** L'appel en cours, ou le dernier terminé avec son retour. C'est ce qui permet de recharger la
   * page en plein appel sans rien perdre. */
  current(): Promise<CallState>;
  /** Décrocher avec ce personnage. Le client parle en premier. */
  start(personaId: string): Promise<CallState>;
  /** Ce que l'élève répond. Le client répond à son tour. */
  say(text: string): Promise<CallState>;
  /** Raccrocher, et recevoir le retour de fin d'appel. */
  hangUp(): Promise<CallState>;
}

export interface AppContext {
  app: { id: string; version: string };
  user: {
    id: string;
    role: 'student' | 'teacher' | 'admin';
    displayName: string;
    impersonatedBy?: { id: string; mode: 'view' | 'act' };
  };
  class: { id: string; name: string } | null;
  group: { id: string; name: string } | null;
  scope: Scope;
  workspace: {
    id: string;
    kind: WorkspaceKind;
    readOnly: boolean;
    isDemo: boolean;
    isSandbox: boolean;
    generation: number;
  } | null;
  scopes: { class: { workspaceId: string } | null; activity: { workspaceId: string } | null };
  activity: { id: string; kind: 'mission' | 'free' | 'interactive' } | null;
  /** Capacité ADR-039, uniquement lorsqu’elle est déclarée dans le manifeste. */
  interactive?: InteractiveCapability;
  /** ADR-055: upload to this app in the workspace resolved by the host. */
  images?: { upload(file: File): Promise<WorkspaceImage> };
  permissions: ReadonlySet<PermissionKey>;
  can(p: PermissionKey): boolean;
  clock: { now(): Date; day(): number };
  navigate: (path: string) => void;
  openApp: (appId: string, path?: string) => void;
  /** Ouvre le panneau CMS du contexte courant, sans commande ni changement de destinataire. */
  openEnvironments: () => void;
  /** Actions des applications activées sur une fiche commune. Le destinataire relit sous la session courante. */
  entityActions?: (
    entity: EntityAction['entity'],
    record: Readonly<Record<string, unknown>>,
  ) => readonly { id: string; label: string; icon: string; open(): void }[];
  /**
   * **LA seule capacité du cœur qu'une application peut appeler — et elle ne prend aucun
   * argument** (ADR-014).
   *
   * Demande la relecture du travail de l'élève dans l'espace de travail COURANT, et rend les
   * remarques : ce que des règles trouvent mal écrit (`wrong`, sûr) et ce qu'un modèle de langue
   * trouve douteux (`doubtful`, une opinion). Voir `WorkReview` ci-dessus pour la façon
   * d'afficher les deux sans les confondre.
   *
   * **Pourquoi aucun paramètre.** C'est la garantie, inscrite dans la signature elle-même : une
   * app ne peut désigner ni un autre élève, ni un autre espace de travail, ni une opération du
   * cœur de son choix — il n'existe aucun endroit où le dire. Le cœur résout l'espace depuis la
   * SESSION de celui qui clique, jamais depuis ce que l'app envoie
   * (`apps/desktop/src/host/reviewWork.ts` pour l'implémentation,
   * `packages/core-domain/src/operations/contacts-review.ts` pour la résolution serveur).
   *
   * **Ce n'est pas un canal générique, et ce n'en deviendra pas un.** Le jour où une app aura
   * besoin d'autre chose du cœur, ce sera une SECONDE capacité nommée, discutée et décidée —
   * jamais un paramètre ajouté ici.
   *
   * Lève, avec un message français déjà écrit par le cœur, quand la demande n'aboutit pas du
   * tout : session expirée, réseau coupé, aucun fichier à relire. Un refus ATTENDU de la partie
   * « douteux » (aucune clé d'IA sur la plateforme, plafond de dépense du jour atteint, usage
   * coupé par l'enseignant) ne lève jamais : il arrive dans `doubtfulUnavailable`, et la partie
   * « mal écrit » est rendue quand même.
   */
  review(): Promise<WorkReview>;
  /**
   * **La SECONDE capacité nommée** (ADR-015) : l'entretien téléphonique simulé. Voir
   * `CallCapability` ci-dessus.
   *
   * Elle existe parce que l'ADR-014 avait écrit d'avance ce qui se passerait le jour où une app
   * aurait besoin d'autre chose : « ce sera une SECONDE capacité nommée, discutée et décidée —
   * jamais un paramètre ajouté à celle-ci ». C'est ce qui a été fait, et `review()` n'a pas bougé
   * d'une virgule.
   */
  call: CallCapability;
}

export interface AppClient {
  Student: ComponentType;
  Teacher?: ComponentType;
  Overlay?: ComponentType;
  TeacherOverlay?: ComponentType;
}

export function defineAppClient(c: AppClient): AppClient {
  return c;
}

// --- Comment une app reçoit son contexte --------------------------------------------------
//
// Le cœur (`apps/desktop`) construit l'`AppContext` et le fournit ici ; l'app le lit par
// `useAppContext()`. C'est le SEUL canal : une app ne reçoit jamais le contexte en props (ses
// composants racines n'en ont aucune, voir `AppClient` ci-dessus), et ne le construit jamais
// elle-même.
//
// Hors d'un hôte NDRC, `useAppContext()` LÈVE plutôt que de renvoyer un contexte vide : un
// contexte factice ferait échouer l'app plus tard, ailleurs, avec un message qui ne dirait rien.

const AppContextValue = createContext<AppContext | null>(null);

export interface AppContextProviderProps {
  value: AppContext;
  children: ReactNode;
}

/** Réservé au cœur : c'est lui qui monte une app, jamais l'inverse. */
export function AppContextProvider({ value, children }: AppContextProviderProps): ReactNode {
  return createElement(AppContextValue.Provider, { value }, children);
}

export function useAppContext(): AppContext {
  const ctx = useContext(AppContextValue);
  if (ctx === null) {
    throw new Error(
      "useAppContext() a été appelé hors du bureau NDRC Labs : ce composant n'est pas monté par " +
        "l'hôte d'application. Vérifiez que le composant est bien exporté par le client.tsx de " +
        "l'app.",
    );
  }
  return ctx;
}
